import { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

interface UseAuthReturn {
  isAuthenticated: boolean;
  user: any;
  token: string | null;
  loading: boolean;
  refreshAuth: () => Promise<void>;
  clearAuth: () => void;
  forceSignOut: () => Promise<void>;
}

export const useAuthGen2 = (): UseAuthReturn => {
  const { user: authenticatorUser, authStatus } = useAuthenticator((context) => [context.user, context.authStatus]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);

      // Check for tokens first, regardless of authStatus, to handle inconsistent state
      try {
        const session = await fetchAuthSession();
        const hasTokens = !!(session.tokens?.accessToken && session.tokens?.idToken);
        
        // If we have valid tokens, we're authenticated regardless of authStatus
        if (hasTokens) {
          
          try {
            const currentUser = await getCurrentUser();
            setUser(currentUser);
          } catch (userError) {
            setUser(authenticatorUser);
          }
          
          setIsAuthenticated(true);
          setToken(session.tokens?.idToken?.toString() || null);
          setLoading(false);
          return;
        }
      } catch (sessionError) {
        // Fall through to check authStatus
      }

      // In Gen 2, we can rely more on the authStatus from useAuthenticator
      if (authStatus === 'authenticated' && authenticatorUser) {
        setUser(authenticatorUser);
        setIsAuthenticated(true);
        setToken(null);
        setLoading(false);
        return;
      }

      // If authStatus is not authenticated
      if (authStatus === 'unauthenticated') {
        setUser(null);
        setIsAuthenticated(false);
        setToken(null);
        setLoading(false);
        return;
      }

      // If authStatus is configuring, keep loading
      if (authStatus === 'configuring') {
        setLoading(true);
        return;
      }

      // Fallback for any other status
      setUser(null);
      setIsAuthenticated(false);
      setToken(null);
      setLoading(false);

    } catch (error) {
      console.error('Gen 2: Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      setToken(null);
      setLoading(false);
    }
  };

  // Check auth status when authStatus or user changes
  useEffect(() => {
    checkAuthStatus();
  }, [authStatus, authenticatorUser]);

  // Timeout fallback to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setIsAuthenticated(false);
        setToken(null);
        setLoading(false);
      }
    }, 3000); // 3 second timeout for Gen 2

    return () => clearTimeout(timeout);
  }, [loading]);

  const refreshAuth = async () => {
    await checkAuthStatus();
  };

  const clearAuth = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setLoading(false);
  };

  const forceSignOut = async () => {
    try {
      const { signOut } = await import('aws-amplify/auth');
      await signOut();
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      // Re-check auth status after sign out
      setTimeout(() => {
        checkAuthStatus();
      }, 1000);
    } catch (error) {
      console.error('Gen 2: Force sign out failed:', error);
    }
  };

  return {
    isAuthenticated,
    user,
    token,
    loading,
    refreshAuth,
    clearAuth,
    forceSignOut
  };
};